import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import MagicLink from "@/pages/MagicLink";
import Home from "@/pages/Home";
import Profile from "@/pages/Profile";
import Team from "@/pages/Team";
import OrgChart from "@/pages/OrgChart";
import AdminUsers from "@/pages/AdminUsers";
import AdminSettings from "@/pages/AdminSettings";
import Placeholder from "@/pages/Placeholder";
import AppShell from "@/components/AppShell";
import Frameworks from "@/pages/Frameworks";
import FrameworkDetail from "@/pages/FrameworkDetail";
import FrameworkBuilder from "@/pages/FrameworkBuilder";
import Cycles from "@/pages/Cycles";
import CycleDetail from "@/pages/CycleDetail";
import GoalsHub from "@/pages/GoalsHub";
import WinsJournal from "@/pages/WinsJournal";
import CoachingLog from "@/pages/CoachingLog";
import CheckIns from "@/pages/CheckIns";
import OneOnOnes from "@/pages/OneOnOnes";
import Reviews from "@/pages/Reviews";
import ReviewDetail from "@/pages/ReviewDetail";
import SignalsHub from "@/pages/SignalsHub";
import Calibration from "@/pages/Calibration";
import CalibrationSession from "@/pages/CalibrationSession";
import Succession from "@/pages/Succession";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import AdminBilling from "@/pages/AdminBilling";
import AdminPlatform from "@/pages/AdminPlatform";
import HelpCenter from "@/pages/HelpCenter";
import TenantPreview from "@/pages/TenantPreview";
import OnboardingWizard from "@/components/OnboardingWizard";

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppShell>
      <OnboardingWizard />
      <Outlet />
    </AppShell>
  );
}

function PublicOnlyLayout() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicOnlyLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/magic/:token" element={<MagicLink />} />
          </Route>
          <Route path="/preview/:subdomain" element={<TenantPreview />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/me" element={<Profile self />} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/team" element={<Team />} />
            <Route path="/org" element={<OrgChart />} />
            <Route path="/goals" element={<GoalsHub />} />
            <Route path="/wins" element={<WinsJournal />} />
            {/* Legacy redirect — Team Goals merged into /goals?scope=team */}
            <Route path="/team-goals" element={<Navigate to="/goals?scope=team" replace />} />
            <Route path="/coaching" element={<CoachingLog />} />
            <Route path="/check-ins" element={<CheckIns />} />
            <Route path="/one-on-ones" element={<OneOnOnes />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/reviews/:id" element={<ReviewDetail />} />
            <Route path="/signals" element={<SignalsHub />} />
            {/* Legacy redirects — Feedback + Recognition merged into Signals */}
            <Route path="/feedback" element={<Navigate to="/signals?tab=feedback" replace />} />
            <Route path="/recognition" element={<Navigate to="/signals?tab=kudos" replace />} />
            <Route path="/calibration" element={<Calibration />} />
            <Route path="/calibration/:id" element={<CalibrationSession />} />
            <Route path="/succession" element={<Succession />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/admin/billing" element={<AdminBilling />} />
            <Route path="/admin/platform" element={<AdminPlatform />} />
            <Route path="/frameworks" element={<Frameworks />} />
            <Route path="/frameworks/:id" element={<FrameworkDetail />} />
            <Route path="/frameworks/:id/edit" element={<FrameworkBuilder />} />
            <Route path="/cycles" element={<Cycles />} />
            <Route path="/cycles/:id" element={<CycleDetail />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/permissions" element={<AdminUsers />} />
            <Route path="/admin/users/invites" element={<AdminUsers />} />
            <Route path="/admin/users/import" element={<AdminUsers />} />
            {/* Legacy redirect — Bulk Import demoted to a tab inside Users & Roles */}
            <Route path="/admin/import" element={<Navigate to="/admin/users/import" replace />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
